import { cssThemedValue } from '@creditornot/cb-ingredients';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { updateOptionBinding } from 'modules/shared-menu/api';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import OptionBindingForm from './OptionBindingForm';
import { useSelectionData } from '../../../../../hooks/useSelectionData';
import { Offer, OptionBinding as OptionBindingT, Option } from '../../../types';
import { OptionBindingHeader } from '../OptionBindingHeader';
import { OptionBindingFormValues } from '../types';
import { optionBindingFormValuesToUpdatePayload } from '../utils';

type Props = {
  draggable: boolean;
  offer: Offer;
  optionBinding: OptionBindingT;
  primaryLanguage: Language;
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Root = styled.div``;

const OpenContent = styled.div`
  animation: ${fadeIn} 0.15s ease-in 1;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

export const OptionBinding = ({ draggable, offer, optionBinding, primaryLanguage }: Props) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();

  const handleToggleOpen = useCallback(() => {
    setOpen(value => !value);
  }, []);

  const { selection, options, updateSelectionData } = useSelectionData();

  const option = options.find(option => option.id === optionBinding.option_id);
  if (!option) {
    throw new Error(`Option \`${optionBinding.option_id}\` not found in global state`);
  }

  const otherItemOptionIds = (offer.option_bindings || [])
    .filter(ob => ob.id !== optionBinding.id)
    .map(ob => ob.option_id);
  const otherItemOptions = otherItemOptionIds
    .map(optionId => options.find(option => option.id === optionId))
    .filter((otherOption): otherOption is Option => otherOption !== undefined)
    .filter(otherOption => otherOption.id !== option.id);

  const { mutateAsync: handleFormSubmit } = useMutation(
    async (formValues: OptionBindingFormValues) => {
      const payload = optionBindingFormValuesToUpdatePayload(primaryLanguage, optionBinding, formValues);
      return updateOptionBinding(selection.id, offer.id, optionBinding.id, payload);
    },
    {
      onSuccess: updatedOffer => {
        toast.success(intl.formatMessage({ id: 'toast.success' }));
        updateSelectionData({ actionType: 'updateOffer', payload: updatedOffer });
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'menu.optionBindingUpdateFailed' }));
      },
      onSettled: () => {
        handleToggleOpen();
      },
    },
  );

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: optionBinding.id,
  });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition],
  );

  return (
    <Root ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <OptionBindingHeader
        offerId={offer.id}
        draggable={draggable}
        dragging={isDragging}
        onToggleOpen={handleToggleOpen}
        option={option}
        optionBindingId={optionBinding.id}
        primaryLanguage={primaryLanguage}
        selectionId={selection.id}
      />

      {open && !isSharedVenue && menuPermissions.contentEdit && (
        <OpenContent>
          <OptionBindingForm
            onSubmit={handleFormSubmit}
            option={option}
            optionBinding={optionBinding}
            otherItemOptions={otherItemOptions}
            primaryLanguage={selection.primary_language}
          />
        </OpenContent>
      )}
    </Root>
  );
};
